import type { IEventBus } from "@/domains/events"
import {
  LockNftSwitchedEvent,
  LockTransferredOutEvent,
  LockTokensWithdrawnEvent,
} from "@/domains/lock/events"

export class SelectedLockOrchestrator {
  private updateSelectedLockId: ((lockId: number | null) => void) | null = null
  private getCurrentSelectedLockId: (() => number | null) | null = null
  private nftSwitchedHandler: ((event: LockNftSwitchedEvent) => void) | undefined
  private lockTransferredHandler: ((event: LockTransferredOutEvent) => void) | undefined
  private lockWithdrawnHandler: ((event: LockTokensWithdrawnEvent) => void) | undefined

  constructor(private eventBus: IEventBus) {
    // Subscribe to NFT switch events
    this.nftSwitchedHandler = this.handleNftSwitched.bind(this)
    this.eventBus.subscribe(LockNftSwitchedEvent.TYPE, this.nftSwitchedHandler)

    // Subscribe to lock transfer events
    this.lockTransferredHandler = this.handleLockTransferred.bind(this)
    this.eventBus.subscribe(LockTransferredOutEvent.TYPE, this.lockTransferredHandler)

    // Subscribe to lock withdrawal events
    this.lockWithdrawnHandler = this.handleLockWithdrawn.bind(this)
    this.eventBus.subscribe(LockTokensWithdrawnEvent.TYPE, this.lockWithdrawnHandler)
  }

  setUpdateSelectedLockIdCallback(
    updateCallback: (lockId: number | null) => void,
    getCurrentCallback: () => number | null,
  ): void {
    this.updateSelectedLockId = updateCallback
    this.getCurrentSelectedLockId = getCurrentCallback
  }

  private handleNftSwitched(event: LockNftSwitchedEvent): void {
    if (this.updateSelectedLockId) {
      this.updateSelectedLockId(event.toNftId)
    }
  }

  private handleLockTransferred(event: LockTransferredOutEvent): void {
    if (this.updateSelectedLockId && this.getCurrentSelectedLockId) {
      // Clear selection if the transferred lock matches the currently selected one
      const currentId = this.getCurrentSelectedLockId()
      if (currentId === event.lockId) {
        this.updateSelectedLockId(null)
      }
    }
  }

  private handleLockWithdrawn(event: LockTokensWithdrawnEvent): void {
    if (this.updateSelectedLockId && this.getCurrentSelectedLockId) {
      // Clear selection if the withdrawn lock matches the currently selected one
      const currentId = this.getCurrentSelectedLockId()
      if (currentId === event.tokenId) {
        this.updateSelectedLockId(null)
      }
    }
  }

  destroy(): void {
    // Unsubscribe from events
    if (this.nftSwitchedHandler) {
      this.eventBus.unsubscribe(LockNftSwitchedEvent.TYPE, this.nftSwitchedHandler)
    }
    if (this.lockTransferredHandler) {
      this.eventBus.unsubscribe(LockTransferredOutEvent.TYPE, this.lockTransferredHandler)
    }
    if (this.lockWithdrawnHandler) {
      this.eventBus.unsubscribe(LockTokensWithdrawnEvent.TYPE, this.lockWithdrawnHandler)
    }
  }
}

